import { useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Shield, 
  Database, 
  Share2, 
  Trash2, 
  Cookie, 
  Mail, 
  Instagram,
  ArrowLeft,
  Lock,
  Eye,
  Users,
  FileText
} from "lucide-react";
import "@/App.css";

// Logo Dive Master
const DIVE_MASTER_LOGO = "https://customer-assets.emergentagent.com/job_aposta-hexa-mar/artifacts/s4a9kcqd_LOGO%20NOVA%20DIVE%20MASTER.png";

function PoliticaPrivacidade() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8fafc] to-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#002776] via-[#001a4d] to-[#002776] text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <ArrowLeft className="w-5 h-5" />
              <img src={DIVE_MASTER_LOGO} alt="Dive Master" className="w-10 h-10 object-contain" />
              <span className="font-heading text-lg">DIVE MASTER</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Title */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#009C3B] to-[#007830] flex items-center justify-center mx-auto mb-4">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-[#002776] mb-4">
            POLÍTICA DE PRIVACIDADE
          </h1>
          <p className="text-gray-600">
            Última atualização: {new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Introduction */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-100">
          <p className="text-gray-700 leading-relaxed">
            A <strong className="text-[#002776]">Dive Master Mergulho</strong> ("nós", "nosso" ou "empresa"), 
            inscrita no CNPJ 50.990.553/0001-07, com sede em Porto Belo - SC, está comprometida em proteger 
            a privacidade e os dados pessoais de nossos usuários. Esta Política de Privacidade descreve como 
            coletamos, usamos, armazenamos e protegemos suas informações quando você utiliza nosso site e serviços.
          </p>
        </section>

        {/* Section 1 - Data Collection */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#002776]/10 flex items-center justify-center">
              <Database className="w-6 h-6 text-[#002776]" />
            </div>
            <h2 className="font-heading text-2xl text-[#002776]">1. DADOS QUE COLETAMOS</h2>
          </div>
          
          <p className="text-gray-700 mb-4">Podemos coletar os seguintes tipos de informações:</p>
          
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-bold text-[#002776] mb-2">Informações fornecidas por você:</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Nome completo</li>
                <li>Endereço de e-mail</li>
                <li>Número de telefone/WhatsApp</li>
                <li>Informações de reserva e preferências de passeio</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-bold text-[#002776] mb-2">Informações coletadas automaticamente:</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Endereço IP e localização aproximada</li>
                <li>Tipo de navegador e dispositivo</li>
                <li>Páginas visitadas e tempo de permanência</li>
                <li>Dados de cookies e tecnologias similares</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-bold text-[#002776] mb-2">Informações de redes sociais:</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Dados públicos do perfil do Instagram (quando autorizado)</li>
                <li>Interações com nosso conteúdo em redes sociais</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 2 - Data Usage */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#009C3B]/10 flex items-center justify-center">
              <Eye className="w-6 h-6 text-[#009C3B]" />
            </div>
            <h2 className="font-heading text-2xl text-[#002776]">2. COMO USAMOS SEUS DADOS</h2>
          </div>
          
          <p className="text-gray-700 mb-4">Utilizamos suas informações para:</p>
          
          <ul className="space-y-3">
            {[
              { id: "uso-processar", text: "Processar reservas e fornecer nossos serviços de passeios" },
              { id: "uso-contato", text: "Entrar em contato sobre sua reserva ou promoções" },
              { id: "uso-atualizacoes", text: "Enviar atualizações sobre o Desafio do Hexa e outras campanhas" },
              { id: "uso-melhorar", text: "Melhorar nosso site e experiência do usuário" },
              { id: "uso-conteudo", text: "Exibir conteúdo personalizado do nosso Instagram" },
              { id: "uso-legal", text: "Cumprir obrigações legais e regulatórias" },
              { id: "uso-seguranca", text: "Prevenir fraudes e garantir a segurança do site" }
            ].map((item) => (
              <li key={item.id} className="flex items-start gap-3 text-gray-600">
                <div className="w-5 h-5 rounded-full bg-[#009C3B] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs">✓</span>
                </div>
                {item.text}
              </li>
            ))}
          </ul>
        </section>

        {/* Section 3 - Data Sharing */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#FFDF00]/20 flex items-center justify-center">
              <Share2 className="w-6 h-6 text-[#002776]" />
            </div>
            <h2 className="font-heading text-2xl text-[#002776]">3. COMPARTILHAMENTO DE DADOS</h2>
          </div>
          
          <p className="text-gray-700 mb-4">
            <strong>Não vendemos seus dados pessoais.</strong> Podemos compartilhar informações com:
          </p>
          
          <div className="space-y-4">
            <div className="border-l-4 border-[#009C3B] pl-4">
              <h3 className="font-bold text-[#002776]">Parceiros de serviço</h3>
              <p className="text-gray-600 text-sm">Processadores de pagamento, serviços de hospedagem e ferramentas de análise.</p>
            </div>
            
            <div className="border-l-4 border-[#009C3B] pl-4">
              <h3 className="font-bold text-[#002776]">Meta/Facebook e Instagram</h3>
              <p className="text-gray-600 text-sm">Para integração com redes sociais e exibição de conteúdo, conforme suas permissões.</p>
            </div>
            
            <div className="border-l-4 border-[#009C3B] pl-4">
              <h3 className="font-bold text-[#002776]">Autoridades legais</h3>
              <p className="text-gray-600 text-sm">Quando exigido por lei ou para proteger nossos direitos legais.</p>
            </div>
          </div>
        </section>

        {/* Section 4 - Instagram/Meta Integration */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 via-pink-500/20 to-orange-500/20 flex items-center justify-center">
              <Instagram className="w-6 h-6 text-[#002776]" />
            </div>
            <h2 className="font-heading text-2xl text-[#002776]">4. INTEGRAÇÃO COM INSTAGRAM/META</h2>
          </div>
          
          <p className="text-gray-700 mb-4">
            Nosso site utiliza a <strong>API do Instagram (Meta)</strong> para exibir conteúdo de nossa conta oficial. 
            Esta integração:
          </p>
          
          <ul className="space-y-3 mb-4">
            {[
              { id: "api-publico", text: "Acessa apenas dados públicos de nossa própria conta" },
              { id: "api-nao-coleta", text: "Não coleta dados pessoais de visitantes através da API" },
              { id: "api-exibe", text: "Exibe fotos e informações de nossos passeios" },
              { id: "api-conformidade", text: "Está em conformidade com as políticas da Meta Platform" }
            ].map((item) => (
              <li key={item.id} className="flex items-start gap-3 text-gray-600">
                <div className="w-5 h-5 rounded-full bg-[#009C3B] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs">✓</span>
                </div>
                {item.text}
              </li>
            ))}
          </ul>
          
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Nota:</strong> Para mais informações sobre como a Meta trata dados, consulte a{" "}
              <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener noreferrer" className="underline">
                Política de Privacidade da Meta
              </a>.
            </p>
          </div>
        </section>

        {/* Section 5 - User Rights */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#002776]/10 flex items-center justify-center">
              <Users className="w-6 h-6 text-[#002776]" />
            </div>
            <h2 className="font-heading text-2xl text-[#002776]">5. SEUS DIREITOS</h2>
          </div>
          
          <p className="text-gray-700 mb-4">
            De acordo com a Lei Geral de Proteção de Dados (LGPD), você tem direito a:
          </p>
          
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { id: "direito-acessar", icon: Eye, title: "Acessar", desc: "Solicitar cópia de seus dados pessoais" },
              { id: "direito-corrigir", icon: FileText, title: "Corrigir", desc: "Atualizar informações incorretas" },
              { id: "direito-excluir", icon: Trash2, title: "Excluir", desc: "Solicitar a exclusão de seus dados" },
              { id: "direito-revogar", icon: Lock, title: "Revogar", desc: "Retirar consentimento a qualquer momento" }
            ].map((item) => (
              <div key={item.id} className="bg-gray-50 rounded-xl p-4 flex items-start gap-3">
                <item.icon className="w-5 h-5 text-[#009C3B] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-[#002776]">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-gray-600 text-sm mt-4">
            Para exercer qualquer desses direitos, entre em contato conosco pelos canais indicados abaixo.
          </p>
        </section>

        {/* Section 6 - Data Deletion */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="font-heading text-2xl text-[#002776]">6. EXCLUSÃO DE DADOS</h2>
          </div>
          
          <p className="text-gray-700 mb-4">
            Você pode solicitar a exclusão de seus dados pessoais a qualquer momento. Para isso:
          </p>
          
          <div className="bg-gray-50 rounded-xl p-6">
            <ol className="list-decimal list-inside space-y-2 text-gray-600">
              <li>Envie um e-mail para <strong className="text-[#002776]">contato@divemastermergulho.com.br</strong></li>
              <li>Inclua no assunto: "Solicitação de Exclusão de Dados"</li>
              <li>Informe seu nome completo e e-mail cadastrado</li>
              <li>Responderemos em até 15 dias úteis</li>
            </ol>
          </div>
          
          <p className="text-gray-500 text-sm mt-4">
            Alguns dados podem ser retidos por obrigações legais ou para fins de prestação de contas.
          </p>
        </section>

        {/* Section 7 - Cookies */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#FFDF00]/20 flex items-center justify-center">
              <Cookie className="w-6 h-6 text-[#002776]" />
            </div>
            <h2 className="font-heading text-2xl text-[#002776]">7. COOKIES E TECNOLOGIAS</h2>
          </div>
          
          <p className="text-gray-700 mb-4">
            Utilizamos cookies e tecnologias similares para:
          </p>
          
          <ul className="space-y-2 text-gray-600 mb-4">
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#009C3B]"></span>
              Manter você conectado e lembrar suas preferências
            </li>
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#009C3B]"></span>
              Analisar o tráfego e desempenho do site
            </li>
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#009C3B]"></span>
              Exibir conteúdo personalizado
            </li>
          </ul>
          
          <p className="text-gray-600 text-sm">
            Você pode gerenciar cookies nas configurações do seu navegador.
          </p>
        </section>

        {/* Section 8 - Security */}
        <section className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#009C3B]/10 flex items-center justify-center">
              <Lock className="w-6 h-6 text-[#009C3B]" />
            </div>
            <h2 className="font-heading text-2xl text-[#002776]">8. SEGURANÇA DOS DADOS</h2>
          </div>
          
          <p className="text-gray-700">
            Implementamos medidas de segurança técnicas e organizacionais para proteger seus dados, 
            incluindo criptografia, controle de acesso e monitoramento contínuo. No entanto, nenhum 
            método de transmissão pela internet é 100% seguro, e não podemos garantir segurança absoluta.
          </p>
        </section>

        {/* Section 9 - Contact */}
        <section className="bg-gradient-to-br from-[#002776] to-[#001a4d] rounded-2xl shadow-lg p-8 mb-8 text-white">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
              <Mail className="w-6 h-6 text-[#FFDF00]" />
            </div>
            <h2 className="font-heading text-2xl">9. CONTATO</h2>
          </div>
          
          <p className="text-white/90 mb-6">
            Para dúvidas, solicitações ou reclamações sobre esta Política de Privacidade ou o tratamento 
            de seus dados pessoais, entre em contato:
          </p>
          
          <div className="space-y-3">
            <div className="bg-white/10 rounded-xl p-4">
              <p className="font-bold text-[#FFDF00]">Dive Master Mergulho</p>
              <p className="text-white/80 text-sm">CNPJ: 50.990.553/0001-07</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-white/80 text-sm">Rua José Alexandre Rocha, n63</p>
              <p className="text-white/80 text-sm">Porto Belo - SC - Brasil</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-white/80 text-sm">E-mail: contato@divemastermergulho.com.br</p>
              <p className="text-white/80 text-sm">WhatsApp: (47) 99792-0909</p>
            </div>
          </div>
        </section>

        {/* Section 10 - Updates */}
        <section className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#002776]/10 flex items-center justify-center">
              <FileText className="w-6 h-6 text-[#002776]" />
            </div>
            <h2 className="font-heading text-2xl text-[#002776]">10. ALTERAÇÕES NESTA POLÍTICA</h2>
          </div>
          
          <p className="text-gray-700">
            Podemos atualizar esta Política de Privacidade periodicamente. Quaisquer alterações serão 
            publicadas nesta página com a data de atualização. Recomendamos que você revise esta política 
            regularmente para se manter informado sobre como protegemos seus dados.
          </p>
        </section>

        {/* Back to home */}
        <div className="text-center mt-12">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 bg-[#009C3B] hover:bg-[#007830] text-white font-bold py-4 px-8 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Voltar para o site
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0f172a] text-white py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <img src={DIVE_MASTER_LOGO} alt="Dive Master" className="w-12 h-12 object-contain mx-auto mb-3" />
          <p className="text-gray-400 text-sm">
            © 2025 Dive Master Mergulho. Todos os direitos reservados.
          </p>
          <p className="text-gray-500 text-xs mt-2">
            CNPJ: 50.990.553/0001-07 | Porto Belo - SC
          </p>
        </div>
      </footer>
    </div>
  );
}

export default PoliticaPrivacidade;
